import { useEffect, useMemo, useRef, useState } from "react";
import Button from "../../../component/Button";
import Checkbox from "../../../component/CheckBox";

import { useBlocker, useLocation } from "react-router-dom";
import { AxiosInstance } from "../../../@api/api";
import { TermsPopUp } from "../../terms/TermsPopUp";
import { useResponsiveMobile } from "../../../hooks/useResponsiveMobile";
import { useModalStore } from "../../../store/modalStore";

const LeaveModal = ({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: () => void }) => {
  const isMobile = useResponsiveMobile(900);
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[99999]">
      <div className="bg-white gap-7 py-10 px-8 rounded-xl w-[90%] md:w-[589px] h-[80%] mt-10 md:mt-0 md:h-auto justify-center items-center flex flex-col">
        <div className="flex flex-col gap-10 py-7">
          <div className="flex flex-col gap-3">
            <img
              className="self-center"
              src="/assets/images/emoticon/close_emoticon.png"
              alt="emoticon"
              width={48}
            />
            <p className="font-semibold text-center text-xl md:text-[28px] text-[#121319]">
              잠깐만요! <br />
              아직 작성이 끝나지 않았어요
            </p>
          </div>
          <span className="font-normal text-base md:text-[20px] text-[#4D4F58] text-center">
            지금 화면을 벗어나면 입력하신{isMobile && <br />} 내용은 저장되지 않습니다. <br />
            1분만 투자하시면, 더 정확한 상담을{isMobile && <br />} 준비할 수 있어요.
          </span>
        </div>

        <div className="w-full flex gap-3">
          <Button onClick={onCancel} className="w-full text-[#FFF] text-[18px]">
            이어서 작성하기
          </Button>
        </div>
      </div>
    </div>
  );
};

type SingleSelectCardProps = {
  value: string;
  selectedValue: string;
  onChange: (v: string) => void;
  label: string;
  error?: boolean;
  shadow?: boolean;
};

type Errors = {
  business?: boolean;
  goods?: boolean;
  importantNeeds?: boolean;

  settlementPainPoints?: boolean;
  partnerManagementPainPoints?: boolean;
  paymentIntegrationPainPoints?: boolean;
  salesPartnerPainPoints?: boolean;

  urgentlyNeeds?: boolean;
  settlementSize?: boolean;
  channel?: boolean;
  privacy?: boolean;
};

type NeedCode = "A1" | "A2" | "A3" | "A4";

const StepTwo = ({
  setStep,
  form,
}: {
  setStep: (step: number) => void;
  form: {
    company: string;
    representative: string;
    department: string;
    email: string;
    phone: string;
  };
}) => {
  const openModal = useModalStore((s) => s.open);

  const [etc, setEtc] = useState({
    Q1: "",
    Q2: "",
    Q3_1: "",
    Q3_2: "",
    Q3_3: "",
    Q3_4: "",
    Q4: "",
  });

  const [onTerms, setOnTerms] = useState(false);
  const isMobile = useResponsiveMobile(900);
  const ErrorText = ({ show, text }: { show: boolean; text: string }) => {
    if (!show) return null;
    return (
      <span className="text-[13px] leading-[16px]" style={{ color: "#F13221" }}>
        {text}
      </span>
    );
  };

  const ALWAYS_CONFIRM = true;

  const [onModal, setOnModal] = useState(false);

  // ===== state =====
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  const [business, setBusiness] = useState("");
  const [goods, setGoods] = useState("");

  //  Card3 멀티 선택 (A1~A4)
  const [importantNeeds, setImportantNeeds] = useState<NeedCode[]>([]);

  const [urgentlyNeeds, setUrgentlyNeeds] = useState("");
  const [settlementSize, setSettlementSize] = useState("");
  const [channel, setChannel] = useState("");

  //  각 멀티 선택 항목의 “서브답”
  const [settlementPainPoints, setSettlementPainPoints] = useState<string>("");
  const [partnerManagementPainPoints, setPartnerManagementPainPoints] = useState<string>("");
  const [paymentIntegrationPainPoints, setPaymentIntegrationPainPoints] = useState<string>("");
  const [salesPartnerPainPoints, setSalesPartnerPainPoints] = useState<string>("");

  // ===== refs =====
  const q1Ref = useRef<HTMLDivElement | null>(null);
  const q2Ref = useRef<HTMLDivElement | null>(null);
  const q3Ref = useRef<HTMLDivElement | null>(null);
  const q4Ref = useRef<HTMLDivElement | null>(null);
  const q5Ref = useRef<HTMLDivElement | null>(null);
  const q6Ref = useRef<HTMLDivElement | null>(null);

  const settlementPainRef = useRef<HTMLDivElement | null>(null);
  const partnerPainRef = useRef<HTMLDivElement | null>(null);
  const paymentPainRef = useRef<HTMLDivElement | null>(null);
  const salesPainRef = useRef<HTMLDivElement | null>(null);

  const privacyRef = useRef<HTMLInputElement | null>(null);

  const focusSection = (el: HTMLElement | null) => {
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    (el as any).focus?.();
  };

  const clearError = (key: keyof Errors) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // ===== 조건부 필수 매핑 ( 기존 키 불일치 문제 해결: A1~A4로 통일) =====
  const needToSubErrorKey = useMemo(() => {
    return {
      A1: "settlementPainPoints",
      A2: "partnerManagementPainPoints",
      A3: "paymentIntegrationPainPoints",
      A4: "salesPartnerPainPoints",
    } as const;
  }, []);

  const isNeedSubAnswered = (need: NeedCode) => {
    if (need === "A1") return settlementPainPoints !== "";
    if (need === "A2") return partnerManagementPainPoints !== "";
    if (need === "A3") return paymentIntegrationPainPoints !== "";
    if (need === "A4") return salesPartnerPainPoints !== "";
    return true;
  };

  const getNeedSubAnswer = (need: NeedCode) => {
    if (need === "A1") return settlementPainPoints;
    if (need === "A2") return partnerManagementPainPoints;
    if (need === "A3") return paymentIntegrationPainPoints;
    if (need === "A4") return salesPartnerPainPoints;
    return "";
  };

  // ===== cards =====
  const Card = ({
    businessValue,
    content,
    content2,
    error,
  }: {
    businessValue: string;
    content: string;
    content2?: string;
    error?: boolean;
  }) => {
    const isMobile = useResponsiveMobile(900);
    const selected = businessValue === business;
    const borderClass = error
      ? "border-[#F13221]"
      : selected
        ? "border-[#0079FF]"
        : "border-[#DDE0E6]";
    return (
      <div
        className={`flex md:justify-center justify-between items-center flex-row md:flex-col gap-3 p-4 md:p-0 md:h-[160px] flex-1 border-[1px] cursor-pointer ${
          selected ? "bg-[#E2F4FF]" : ""
        } ${borderClass} rounded-lg`}
        onClick={() => {
          setBusiness(businessValue);
          clearError("business");
        }}
      >
        {onTerms && (
          <TermsPopUp
            onConfirm={() => {
              setOnTerms(false);
            }}
            onCancel={() => {
              setOnTerms(false);
            }}
          />
        )}
        {!isMobile && <img src="/assets/images/icon/temp.png" width={24} height={24} alt="" />}

        <div className="flex flex-row gap-1 items-center">
          <img
            src={
              selected
                ? "/assets/images/icon/radio_button_selected.png"
                : "/assets/images/icon/radio_button.png"
            }
            width={18}
            height={18}
            alt=""
            className="h-[18px] w-[18px]"
          />
          <span className="font-normal text-[16px] md:text-base text-[#121319]">{content}</span>
        </div>

        {!isMobile && content2 && (
          <span className="font-normal text-[12px] text-[#121319]">{content2}</span>
        )}

        {isMobile && <img src="/assets/images/icon/temp.png" width={24} height={24} alt="" />}
      </div>
    );
  };

  const Card2 = ({
    value,
    selectedValue,
    onChange,
    label,
    error,
    shadow,
  }: SingleSelectCardProps) => {
    const selected = value === selectedValue;
    const borderClass = error
      ? "border-[#F13221]"
      : selected
        ? "border-[#0079FF]"
        : "border-[#DDE0E6]";
    return (
      <div
        style={{ boxShadow: shadow ? "0px 2px 20px 0px #11509640" : "" }}
        className={`items-center flex border-[1px] ${borderClass} ${
          selected ? "bg-[#E2F4FF]" : "bg-[#FFF]"
        } p-4 flex flex-row gap-[10px] rounded-lg cursor-pointer`}
        onClick={() => onChange(value)}
        role="radio"
        aria-checked={selected}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onChange(value);
        }}
      >
        <img
          className="h-[18px] w-[18px]"
          src={
            selected
              ? "/assets/images/icon/radio_button_selected.png"
              : "/assets/images/icon/radio_button.png"
          }
          alt=""
        />
        <span className="font-normal text-[16px] md:text-base text-[#121319] break-words">
          {label}
        </span>
      </div>
    );
  };

  const toggleImportantNeed = (service: NeedCode) => {
    setImportantNeeds((prev) => {
      const next = prev.includes(service) ? prev.filter((v) => v !== service) : [...prev, service];
      return next;
    });

    clearError("importantNeeds");

    //  해제 시 해당 서브에러도 함께 제거 (중요: prev 기준으로 판단)
    setErrors((prev) => {
      const next = { ...prev };
      const isRemoving = importantNeeds.includes(service);
      if (isRemoving) {
        const subKey = needToSubErrorKey[service] as keyof Errors | undefined;
        if (subKey) delete next[subKey];
      }
      return next;
    });
  };

  const Card3 = ({
    service,
    content,
    error,
  }: {
    service: NeedCode;
    content: string;
    error?: boolean;
  }) => {
    const checked = importantNeeds.includes(service);
    const borderClass = error
      ? "border-[#F13221]"
      : checked
        ? "border-[#0079FF]"
        : "border-[#DDE0E6]";
    return (
      <div
        className={`flex flex-1 border-[1px] ${borderClass} ${
          checked ? "bg-[#E2F4FF]" : ""
        } p-4 flex flex-row gap-3 rounded-lg cursor-pointer`}
        onClick={() => toggleImportantNeed(service)}
      >
        <input
          type="checkbox"
          checked={checked}
          readOnly
          className="
    pointer-events-none
    w-6 h-6
    appearance-none
    border-2 border-[#DDE0E6]
    rounded
    relative

    checked:bg-[#0079FF]
    checked:border-[#0079FF]

    checked:after:content-['']
    checked:after:absolute
    checked:after:left-[7px]
    checked:after:top-[2px]
    checked:after:w-[6px]
    checked:after:h-[12px]
    checked:after:border-white
    checked:after:border-r-2
    checked:after:border-b-2
    checked:after:rotate-45
  "
        />
        <span className="font-normal text-base text-[#121319]">{content}</span>
      </div>
    );
  };

  // ===== validate =====
  const validateAndFocus = () => {
    const next: Errors = {};
    if (!business) next.business = true;
    if (!goods) next.goods = true;
    if (importantNeeds.length === 0) next.importantNeeds = true;

    for (const need of importantNeeds) {
      //  A1~A4는 모두 서브질문이 존재하는 구조로 보이므로 그대로 체크
      const subKey = needToSubErrorKey[need] as keyof Errors;
      if (!isNeedSubAnswered(need)) next[subKey] = true;
    }

    if (!urgentlyNeeds) next.urgentlyNeeds = true;
    if (!settlementSize) next.settlementSize = true;
    if (!channel) next.channel = true;
    if (!agreePrivacy) next.privacy = true;

    setErrors(next);

    if (next.business) return (focusSection(q1Ref.current), false);
    if (next.goods) return (focusSection(q2Ref.current), false);
    if (next.importantNeeds) return (focusSection(q3Ref.current), false);

    if (next.settlementPainPoints) return (focusSection(settlementPainRef.current), false);
    if (next.partnerManagementPainPoints) return (focusSection(partnerPainRef.current), false);
    if (next.paymentIntegrationPainPoints) return (focusSection(paymentPainRef.current), false);
    if (next.salesPartnerPainPoints) return (focusSection(salesPainRef.current), false);

    if (next.urgentlyNeeds) return (focusSection(q4Ref.current), false);
    if (next.settlementSize) return (focusSection(q5Ref.current), false);
    if (next.channel) return (focusSection(q6Ref.current), false);

    if (next.privacy) {
      privacyRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      privacyRef.current?.focus();
      return false;
    }

    return true;
  };

  const getNeedSubBody = (need: NeedCode, subAnswerCd: string) => {
    // A1(정산): 기타 = A4 -> etc.Q3_1
    if (need === "A1" && subAnswerCd === "A4") return etc.Q3_1;

    // A2(파트너): 기타 = A4 -> etc.Q3_2
    if (need === "A2" && subAnswerCd === "A4") return etc.Q3_2;

    // A3(결제연동): 기타 = A5 -> etc.Q3_3
    if (need === "A3" && subAnswerCd === "A5") return etc.Q3_3;

    // A4(영업대행): 기타 = A4 -> etc.Q3_4
    if (need === "A4" && subAnswerCd === "A4") return etc.Q3_4;

    return undefined;
  };

  //  Q3 멀티선택 + Q3-n(서브답) payload 생성
  const buildQnaList = () => {
    const qnaList: Array<{
      questionCd: string;
      answerCd: string;
      body?: string;
    }> = [];

    qnaList.push({
      questionCd: "Q1",
      answerCd: business,
      body: business === "A5" ? etc.Q1 : undefined,
    });

    qnaList.push({
      questionCd: "Q2",
      answerCd: goods,
      body: goods === "A4" ? etc.Q2 : undefined,
    });

    qnaList.push({
      questionCd: "Q3",
      answerCd: importantNeeds.join(","),
    });

    //  Q3-1, Q3-2, ... : 선택된 순서대로 서브답 + 기타 입력이면 body 채움
    importantNeeds.forEach((need, idx) => {
      const sub = getNeedSubAnswer(need);
      if (!sub) return;

      const body = getNeedSubBody(need, sub);

      qnaList.push({
        questionCd: `Q3-${idx + 1}`,
        answerCd: sub,
        body, //  기타일 때만 값, 아니면 undefined
      });
    });

    qnaList.push({
      questionCd: "Q4",
      answerCd: urgentlyNeeds,
      body: urgentlyNeeds === "A5" ? etc.Q4 : undefined, //  Q4 기타 입력 반영
    });

    qnaList.push({ questionCd: "Q5", answerCd: settlementSize });
    qnaList.push({ questionCd: "Q6", answerCd: channel });

    return qnaList;
  };

  const { pathname, search } = useLocation();

  const submit = async () => {
    try {
      const qnaList = buildQnaList();

      await AxiosInstance.post("/api/v1/createAdoptForm", {
        formCd:
          search.split("=")[1] === "introduction"
            ? 1
            : search.split("=")[1] === "adoption"
              ? 0
              : "",
        coNm: form.company,
        name: form.representative,
        deptNm: form.department,
        telNo: form.phone,
        email: form.email,
        termsYn: 1,
        qnaList,
      });
    } catch (error) {}
  };

  const handleSubmit = () => {
    const ok = validateAndFocus();
    if (!ok) return;
    isAllowLeavingRef.current = true;

    submit();

    setStep(3);
  };

  // ===== enabled state =====
  const isValid = useMemo(() => {
    if (!business) return false;
    if (!goods) return false;
    if (importantNeeds.length === 0) return false;

    for (const need of importantNeeds) {
      if (!isNeedSubAnswered(need)) return false;
    }

    if (!urgentlyNeeds) return false;
    if (!settlementSize) return false;
    if (!channel) return false;
    if (!agreePrivacy) return false;

    return true;
  }, [
    business,
    goods,
    importantNeeds,
    urgentlyNeeds,
    settlementSize,
    channel,
    agreePrivacy,
    settlementPainPoints,
    partnerManagementPainPoints,
    paymentIntegrationPainPoints,
    salesPartnerPainPoints,
    needToSubErrorKey,
  ]);

  const isDirty = useMemo(() => {
    return (
      business !== "" ||
      goods !== "" ||
      importantNeeds.length > 0 ||
      urgentlyNeeds !== "" ||
      settlementSize !== "" ||
      channel !== "" ||
      agreePrivacy
    );
  }, [business, goods, importantNeeds, urgentlyNeeds, settlementSize, channel, agreePrivacy]);

  // =========================
  //  라우팅(앱 내부 이동) 차단: 커스텀 모달
  // =========================
  const isAllowLeavingRef = useRef(false);

  const shouldBlock = useMemo(() => {
    if (isAllowLeavingRef.current) return false;
    if (ALWAYS_CONFIRM) return true;
    return isDirty;
  }, [isDirty, ALWAYS_CONFIRM]);

  const blocker = useBlocker(shouldBlock);

  const handleLeaveCancel = () => {
    setOnModal(false);
    const anyBlocker = blocker as any;
    if (typeof anyBlocker.reset === "function") {
      anyBlocker.reset();
    }
  };

  const handleLeaveConfirm = () => {
    setOnModal(false);
    isAllowLeavingRef.current = true;

    const anyBlocker = blocker as any;
    if (typeof anyBlocker.proceed === "function") {
      anyBlocker.proceed();
    }
  };

  useEffect(() => {
    if (blocker.state === "blocked") {
      setOnModal(true);
    }
  }, [blocker.state]);

  // =========================
  //  브라우저 이탈(새로고침/탭닫기/주소창 이동): 기본 confirm
  // =========================
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isAllowLeavingRef.current) return;
      if (!ALWAYS_CONFIRM && !isDirty) return;

      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty, ALWAYS_CONFIRM]);

  return (
    <div className="w-full md:max-w-[1012px] rounded-3xl bg-white flex flex-col gap-10 p-8 md:gap-[50px] md:p-[100px] shadow-[0_4px_80px_0_rgba(0,0,0,0.1)]">
      {onModal && <LeaveModal onCancel={handleLeaveCancel} onConfirm={handleLeaveConfirm} />}

      <div className="flex flex-col gap-10 md:gap-[50px] items-center justify-center md:items-start">
        <img width={isMobile ? 44 : 78} src="/assets/images/logo/logo_blue.png" alt="logo" />
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-6 justify-center items-center md:items-start">
            <img
              width={isMobile ? 44 : 66}
              src="/assets/images/icon/application.png"
              alt="application"
            />
            <span className="text-center md:text-left font-bold text-[24px] md:text-display text-[#121319]">
              간단한 설문으로 <br />
              <span className="text-[#0079FF]">더 정확한 상담</span>을 받아보세요
            </span>
          </div>
          <span className="font-normal text-[#4D4F58] text-[16px] md:text-[20px] text-center md:text-left">
            고객님 상황에 맞춘 상담을 준비해드릴게요.
            <br /> 1분만 답해주시면, 꼭 필요한 자료와 담당자를 {isMobile && <br />} 바로
            안내해드려요.
          </span>
        </div>
      </div>

      {/* Q1 */}
      <div ref={q1Ref} tabIndex={-1} className="flex flex-col gap-6 rounded-2xl outline-none">
        <span className="font-normal text-[18px] md:text-heading1_2 text-[#121319]">
          Q. 귀사의 주된 사업 영역은 무엇입니까?
        </span>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col md:flex-row gap-3">
            <Card businessValue="A1" content="재정산 대행" error={!!errors.business} />
            <Card businessValue="A2" content="프랜차이즈 본사" error={!!errors.business} />
            <Card
              businessValue="A3"
              content="O2O 온라인 플랫폼"
              content2="오픈마켓"
              error={!!errors.business}
            />
          </div>
          <div className="flex flex-col md:flex-row gap-3">
            <Card businessValue="A4" content="스타트업" error={!!errors.business} />
            <Card businessValue="A5" content="기타" error={!!errors.business} />
          </div>
          {business === "A5" && (
            <input
              onChange={(e) => {
                setEtc((prev) => ({ ...prev, Q1: e.target.value }));
              }}
              placeholder="내용을 입력해주세요"
              className="w-full max-w-full min-w-0 box-border min-h-[56px] border-[1px] p-4 border-[#DDE0E6] rounded-lg focus:border-[1px] focus:border-[#0079FF] focus:outline-none"
            />
          )}
          <ErrorText show={!!errors.business} text="사업영역을 선택해주세요" />
        </div>
      </div>

      {/* Q2 */}
      <div ref={q2Ref} tabIndex={-1} className="flex flex-col gap-6 outline-none">
        <span className="font-normal text-[18px] md:text-heading1_2 text-[#121319] break-keep">
          Q. 귀사의 최종 고객이 취급하는 상품이 무엇입니까 ?
        </span>
        <div className="flex flex-col gap-3">
          <div className="md:grid md:grid-cols-2 gap-3 flex flex-col">
            <Card2
              value="A1"
              selectedValue={goods}
              error={!!errors.goods}
              onChange={(v) => {
                setGoods(v);
                clearError("goods");
              }}
              label="옷, 음식/음료, 전자제품 등 실물상품"
            />
            <Card2
              value="A2"
              selectedValue={goods}
              onChange={(v) => {
                setGoods(v);
                clearError("goods");
              }}
              label="숙박,렌탈,컨설팅 등 예약/대여 서비스"
              error={!!errors.goods}
            />
            <Card2
              value="A3"
              selectedValue={goods}
              onChange={(v) => {
                setGoods(v);
                clearError("goods");
              }}
              label="교육, 광고 등 무형의 서비스"
              error={!!errors.goods}
            />
            <Card2
              value="A4"
              selectedValue={goods}
              onChange={(v) => {
                setGoods(v);
                clearError("goods");
              }}
              label="기타(직접 입력)"
              error={!!errors.goods}
            />
          </div>
          {goods === "A4" && (
            <input
              onChange={(e) => {
                setEtc((prev) => ({ ...prev, Q2: e.target.value }));
              }}
              placeholder="내용을 입력해주세요"
              className="w-full max-w-full min-w-0 box-border min-h-[56px] border-[1px] p-4 border-[#DDE0E6] rounded-lg focus:border-[1px] focus:border-[#0079FF] focus:outline-none"
            />
          )}
          <ErrorText show={!!errors.goods} text="취급하는 상품을 선택해주세요" />
        </div>
      </div>

      {/* Q3 */}
      <div ref={q3Ref} tabIndex={-1} className="flex flex-col rounded-2xl outline-none gap-3">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            <span className="font-normal text-[18px] md:text-heading1_2 text-[#121319] break-keep">
              Q. 현재 귀사가 가장 중요하게 필요한 기능/서비스를 선택해 주세요.
            </span>
            <span className="font-normal text-[14px] md:text-[18px] text-[#4D4F58]">
              (복수 선택 가능)
            </span>
          </div>

          <div className="flex flex-col gap-3">
            <Card3 error={!!errors.importantNeeds} content="정산 업무의 자동화" service="A1" />
            {importantNeeds.includes("A1") && (
              <div
                ref={settlementPainRef}
                tabIndex={-1}
                className={`flex flex-col gap-6 bg-[#F2F4F8] rounded-2xl p-3 md:p-6 outline-none ${
                  errors.settlementPainPoints ? "border border-red-500 bg-red-50" : ""
                }`}
              >
                <span className="font-normal text-[16px] md:text-[18px] text-[#121319]">
                  Q. 해당 필요를 충족시키지 못하는 구체적인 고충은 무엇인가요?
                </span>

                <div className="md:grid md:grid-cols-2 gap-3 flex flex-col">
                  <Card2
                    shadow={isMobile ? false : true}
                    error={!!errors.settlementPainPoints}
                    label="과도한 정산 업무"
                    onChange={(v) => {
                      setSettlementPainPoints(v);
                      clearError("settlementPainPoints");
                    }}
                    selectedValue={settlementPainPoints}
                    value="A1"
                  />
                  <Card2
                    shadow={isMobile ? false : true}
                    error={!!errors.settlementPainPoints}
                    label="정산 사고 리스크"
                    onChange={(v) => {
                      setSettlementPainPoints(v);
                      clearError("settlementPainPoints");
                    }}
                    selectedValue={settlementPainPoints}
                    value="A2"
                  />
                  <Card2
                    shadow={isMobile ? false : true}
                    error={!!errors.settlementPainPoints}
                    label="정산관련 소통"
                    onChange={(v) => {
                      setSettlementPainPoints(v);
                      clearError("settlementPainPoints");
                    }}
                    selectedValue={settlementPainPoints}
                    value="A3"
                  />
                  <Card2
                    shadow={isMobile ? false : true}
                    error={!!errors.settlementPainPoints}
                    label="기타 (직접 입력)"
                    onChange={(v) => {
                      setSettlementPainPoints(v);
                      clearError("settlementPainPoints");
                    }}
                    selectedValue={settlementPainPoints}
                    value="A4"
                  />
                </div>

                {settlementPainPoints === "A4" && (
                  <textarea
                    onChange={(e) => {
                      setEtc((prev) => ({ ...prev, Q3_1: e.target.value }));
                    }}
                    placeholder="내용을 입력해주세요."
                    className="min-w-0 w-full min-h-[120px] flex flex-1 rounded-lg p-4"
                  />
                )}
              </div>
            )}

            <Card3 error={!!errors.importantNeeds} content="파트너/가맹점 관리" service="A2" />
            {importantNeeds.includes("A2") && (
              <div
                ref={partnerPainRef}
                tabIndex={-1}
                className={`flex flex-col gap-6 bg-[#F2F4F8] rounded-2xl p-3 md:p-6 outline-none ${
                  errors.partnerManagementPainPoints ? "border border-red-500 bg-red-50" : ""
                }`}
              >
                <span className="font-normal text-[16px] md:text-[18px] text-[#121319]">
                  Q. 해당 필요를 충족시키지 못하는 구체적인 고충은 무엇인가요?
                </span>

                <div className="md:grid md:grid-cols-2 gap-3 flex flex-col">
                  <Card2
                    shadow={isMobile ? false : true}
                    error={!!errors.partnerManagementPainPoints}
                    label="파트너 관리툴 경쟁력 부족"
                    onChange={(v) => {
                      setPartnerManagementPainPoints(v);
                      clearError("partnerManagementPainPoints");
                    }}
                    selectedValue={partnerManagementPainPoints}
                    value="A1"
                  />
                  <Card2
                    shadow={isMobile ? false : true}
                    error={!!errors.partnerManagementPainPoints}
                    label="정산 정보 공유 과정이 비효율적임"
                    onChange={(v) => {
                      setPartnerManagementPainPoints(v);
                      clearError("partnerManagementPainPoints");
                    }}
                    selectedValue={partnerManagementPainPoints}
                    value="A2"
                  />
                  <Card2
                    shadow={isMobile ? false : true}
                    error={!!errors.partnerManagementPainPoints}
                    label="파트너 관리 업무 체계가 미흡함"
                    onChange={(v) => {
                      setPartnerManagementPainPoints(v);
                      clearError("partnerManagementPainPoints");
                    }}
                    selectedValue={partnerManagementPainPoints}
                    value="A3"
                  />
                  <Card2
                    shadow={isMobile ? false : true}
                    error={!!errors.partnerManagementPainPoints}
                    label="기타 (직접 입력)"
                    onChange={(v) => {
                      setPartnerManagementPainPoints(v);
                      clearError("partnerManagementPainPoints");
                    }}
                    selectedValue={partnerManagementPainPoints}
                    value="A4"
                  />
                </div>

                {partnerManagementPainPoints === "A4" && (
                  <textarea
                    onChange={(e) => {
                      setEtc((prev) => ({ ...prev, Q3_2: e.target.value }));
                    }}
                    placeholder="내용을 입력해주세요."
                    className="min-w-0 w-full min-h-[120px] flex flex-1 rounded-lg p-4"
                  />
                )}
              </div>
            )}

            <Card3
              error={!!errors.importantNeeds}
              content="다양한 결제 수단 및 시스템 연동"
              service="A3"
            />
            {importantNeeds.includes("A3") && (
              <div
                ref={paymentPainRef}
                tabIndex={-1}
                className={`flex flex-col gap-6 bg-[#F2F4F8] rounded-2xl p-3 md:p-6 outline-none ${
                  errors.paymentIntegrationPainPoints ? "border border-red-500 bg-red-50" : ""
                }`}
              >
                <span className="font-normal text-[16px] md:text-[18px] text-[#121319]">
                  Q. 해당 필요를 충족시키지 못하는 구체적인 고충은 무엇인가요?
                </span>

                <div className="md:grid md:grid-cols-2 gap-3 flex flex-col">
                  <Card2
                    shadow={isMobile ? false : true}
                    error={!!errors.paymentIntegrationPainPoints}
                    label="복잡한 수수료 계산으로 인해 시간투입이 과도함"
                    onChange={(v) => {
                      setPaymentIntegrationPainPoints(v);
                      clearError("paymentIntegrationPainPoints");
                    }}
                    selectedValue={paymentIntegrationPainPoints}
                    value="A1"
                  />
                  <Card2
                    shadow={isMobile ? false : true}
                    error={!!errors.paymentIntegrationPainPoints}
                    label="복잡한 수수료 계산으로 인해 계산 오류 발생이 빈번함"
                    onChange={(v) => {
                      setPaymentIntegrationPainPoints(v);
                      clearError("paymentIntegrationPainPoints");
                    }}
                    selectedValue={paymentIntegrationPainPoints}
                    value="A2"
                  />
                  <Card2
                    shadow={isMobile ? false : true}
                    error={!!errors.paymentIntegrationPainPoints}
                    label="단계별 정산 내역을 공유 과정이 비효율적임"
                    onChange={(v) => {
                      setPaymentIntegrationPainPoints(v);
                      clearError("paymentIntegrationPainPoints");
                    }}
                    selectedValue={paymentIntegrationPainPoints}
                    value="A3"
                  />
                  <Card2
                    shadow={isMobile ? false : true}
                    error={!!errors.paymentIntegrationPainPoints}
                    label="영업대행 관리 업무 체계가 미흡함"
                    onChange={(v) => {
                      setPaymentIntegrationPainPoints(v);
                      clearError("paymentIntegrationPainPoints");
                    }}
                    selectedValue={paymentIntegrationPainPoints}
                    value="A4"
                  />
                  <Card2
                    shadow={isMobile ? false : true}
                    error={!!errors.paymentIntegrationPainPoints}
                    label="기타 (직접 입력)"
                    onChange={(v) => {
                      setPaymentIntegrationPainPoints(v);
                      clearError("paymentIntegrationPainPoints");
                    }}
                    selectedValue={paymentIntegrationPainPoints}
                    value="A5"
                  />
                </div>

                {paymentIntegrationPainPoints === "A5" && (
                  <textarea
                    onChange={(e) => {
                      setEtc((prev) => ({ ...prev, Q3_3: e.target.value }));
                    }}
                    placeholder="내용을 입력해주세요."
                    className="min-w-0 w-full min-h-[120px] flex flex-1 rounded-lg p-4"
                  />
                )}
              </div>
            )}

            <Card3
              error={!!errors.importantNeeds}
              content="영업대행 파트너 체계화 및 관리"
              service="A4"
            />
            {importantNeeds.includes("A4") && (
              <div
                ref={salesPainRef}
                tabIndex={-1}
                className={`flex flex-col gap-6 bg-[#F2F4F8] rounded-2xl p-3 md:p-6 outline-none ${
                  errors.salesPartnerPainPoints ? "border border-red-500 bg-red-50" : ""
                }`}
              >
                <span className="font-normal text-[16px] md:text-[18px] text-[#121319]">
                  Q. 해당 필요를 충족시키지 못하는 구체적인 고충은 무엇인가요?
                </span>

                <div className="md:grid md:grid-cols-2 gap-3 flex flex-col">
                  <Card2
                    shadow={isMobile ? false : true}
                    error={!!errors.salesPartnerPainPoints}
                    label="채널별 결제 내역 조회 방법이 불편함"
                    onChange={(v) => {
                      setSalesPartnerPainPoints(v);
                      clearError("salesPartnerPainPoints");
                    }}
                    selectedValue={salesPartnerPainPoints}
                    value="A1"
                  />
                  <Card2
                    shadow={isMobile ? false : true}
                    error={!!errors.salesPartnerPainPoints}
                    label="결제 내역 관리가 비효율적임"
                    onChange={(v) => {
                      setSalesPartnerPainPoints(v);
                      clearError("salesPartnerPainPoints");
                    }}
                    selectedValue={salesPartnerPainPoints}
                    value="A2"
                  />
                  <Card2
                    shadow={isMobile ? false : true}
                    error={!!errors.salesPartnerPainPoints}
                    label="파트너사에게 결제 내역 공유 과정이 비효율적임"
                    onChange={(v) => {
                      setSalesPartnerPainPoints(v);
                      clearError("salesPartnerPainPoints");
                    }}
                    selectedValue={salesPartnerPainPoints}
                    value="A3"
                  />
                  <Card2
                    shadow={isMobile ? false : true}
                    error={!!errors.salesPartnerPainPoints}
                    label="기타 (직접 입력)"
                    onChange={(v) => {
                      setSalesPartnerPainPoints(v);
                      clearError("salesPartnerPainPoints");
                    }}
                    selectedValue={salesPartnerPainPoints}
                    value="A4"
                  />
                </div>

                {salesPartnerPainPoints === "A4" && (
                  <textarea
                    onChange={(e) => {
                      setEtc((prev) => ({ ...prev, Q3_4: e.target.value }));
                    }}
                    placeholder="내용을 입력해주세요."
                    className="min-w-0 w-full min-h-[120px] flex flex-1 rounded-lg p-4"
                  />
                )}
              </div>
            )}
          </div>
        </div>

        <ErrorText show={!!errors.importantNeeds} text="필요한 기능/서비스를 선택해주세요" />
      </div>

      {/* Q4 */}
      <div ref={q4Ref} tabIndex={-1} className="flex flex-col gap-3 rounded-2xl outline-none">
        <div className="flex flex-col gap-6">
          <span className="font-normal text-[18px] md:text-[28px] text-[#121319] break-keep">
            Q. 현재 가장 시급하게 필요하신 것은 무엇인가요?
          </span>
          <div className="md:grid md:grid-cols-2 gap-3 flex flex-col">
            <Card2
              error={!!errors.urgentlyNeeds}
              value="A1"
              selectedValue={urgentlyNeeds}
              onChange={(v) => {
                setUrgentlyNeeds(v);
                clearError("urgentlyNeeds");
              }}
              label="'품' 소개서만 바로 받고 싶습니다."
            />
            <Card2
              error={!!errors.urgentlyNeeds}
              value="A2"
              selectedValue={urgentlyNeeds}
              onChange={(v) => {
                setUrgentlyNeeds(v);
                clearError("urgentlyNeeds");
              }}
              label="구체적인 상담을 원합니다. (도입 검토)"
            />
            <Card2
              error={!!errors.urgentlyNeeds}
              value="A3"
              selectedValue={urgentlyNeeds}
              onChange={(v) => {
                setUrgentlyNeeds(v);
                clearError("urgentlyNeeds");
              }}
              label="가격/비용 구조 확인이 주된 목적입니다."
            />
            <Card2
              error={!!errors.urgentlyNeeds}
              value="A4"
              selectedValue={urgentlyNeeds}
              onChange={(v) => {
                setUrgentlyNeeds(v);
                clearError("urgentlyNeeds");
              }}
              label="단순 서비스 기능 확인 및 시장 조사"
            />
            <Card2
              error={!!errors.urgentlyNeeds}
              value="A5"
              selectedValue={urgentlyNeeds}
              onChange={(v) => {
                setUrgentlyNeeds(v);
                clearError("urgentlyNeeds");
              }}
              label="기타(직접 입력)"
            />
          </div>
          {urgentlyNeeds === "A5" && (
            <input
              onChange={(e) => {
                setEtc((prev) => ({ ...prev, Q4: e.target.value }));
              }}
              placeholder="내용을 입력해주세요"
              className="w-full max-w-full min-w-0 box-border min-h-[56px] border-[1px] p-4 border-[#DDE0E6] rounded-lg focus:border-[1px] focus:border-[#0079FF] focus:outline-none"
            />
          )}
        </div>
        <ErrorText show={!!errors.urgentlyNeeds} text="필요한 것을 선택해주세요" />
      </div>

      {/* Q5 */}
      <div ref={q5Ref} tabIndex={-1} className="flex flex-col gap-3 rounded-2xl outline-none">
        <div className="flex flex-col gap-6">
          <span className="font-normal text-[18px] md:text-[28px] text-[#121319] break-keep">
            Q. 도입 시 예상되는 정산 규모는 어느 정도입니까?
          </span>
          <div className="md:grid md:grid-cols-2 gap-3 flex flex-col">
            <Card2
              error={!!errors.settlementSize}
              value="A1"
              selectedValue={settlementSize}
              onChange={(v) => {
                setSettlementSize(v);
                clearError("settlementSize");
              }}
              label="월 정산 건수 5천 건 미만 (소규모/스타트업)"
            />
            <Card2
              error={!!errors.settlementSize}
              value="A2"
              selectedValue={settlementSize}
              onChange={(v) => {
                setSettlementSize(v);
                clearError("settlementSize");
              }}
              label="월 정산 건수 5천 ~ 3만 건 미만"
            />
            <Card2
              error={!!errors.settlementSize}
              value="A3"
              selectedValue={settlementSize}
              onChange={(v) => {
                setSettlementSize(v);
                clearError("settlementSize");
              }}
              label="월 정산 건수 3만 건 이상 (대규모)"
            />
          </div>
        </div>
        <ErrorText show={!!errors.settlementSize} text="정산규모를 선택해주세요" />
      </div>

      {/* Q6 */}
      <div ref={q6Ref} tabIndex={-1} className="flex flex-col gap-3 rounded-2xl outline-none">
        <div className="flex flex-col gap-6">
          <span className="font-normal text-[18px] md:text-[28px] text-[#121319]">
            Q. 품을 어떤 경로로 알게 되셨나요?
          </span>
          <div className="md:grid md:grid-cols-2 gap-3 flex flex-col">
            <Card2
              error={!!errors.channel}
              value="A1"
              selectedValue={channel}
              onChange={(v) => {
                setChannel(v);
                clearError("channel");
              }}
              label="네이버/구글 검색"
            />
            <Card2
              error={!!errors.channel}
              value="A2"
              selectedValue={channel}
              onChange={(v) => {
                setChannel(v);
                clearError("channel");
              }}
              label="광고 (온라인/오프라인)"
            />
            <Card2
              error={!!errors.channel}
              value="A3"
              selectedValue={channel}
              onChange={(v) => {
                setChannel(v);
                clearError("channel");
              }}
              label="지인/협력사 추천"
            />
            <Card2
              error={!!errors.channel}
              value="A4"
              selectedValue={channel}
              onChange={(v) => {
                setChannel(v);
                clearError("channel");
              }}
              label="관련 세미나/행사"
            />
            <Card2
              error={!!errors.channel}
              value="A5"
              selectedValue={channel}
              onChange={(v) => {
                setChannel(v);
                clearError("channel");
              }}
              label="기존 고객"
            />
          </div>
        </div>
        <ErrorText show={!!errors.channel} text="경로를 선택해주세요" />
      </div>

      {/* 약관 + 버튼 */}
      <div className="flex flex-col gap-6">
        <div className="flex flex-row gap-2 items-center px-2 -m-2 justify-between">
          <Checkbox
            ref={privacyRef}
            id="agreePrivacy"
            checked={agreePrivacy}
            error={!!errors.privacy}
            label={
              <span
                style={{
                  color: errors.privacy ? "#F13221" : "#121319",
                }}
                className="text-[16px]"
              >
                (필수) 서비스 이용을 위한 필수 동의를 선택해주세요
              </span>
            }
            onCheckedChange={(next) => {
              setAgreePrivacy(next);
              if (next) clearError("privacy");
            }}
          />
          <button
            onClick={() => {
              openModal("terms");
            }}
            className={`font-normal text-[14px] md:text-base text-[#0079FF] cursor-pointer flex items-center`}
          >
            내용보기
          </button>
        </div>

        <Button
          id="btn-final-submit"
          className={`h-14 md:h-20 text-base rounded-[12px] md:text-[28px] font-normal ${
            isValid ? "bg-[#0079FF]" : "bg-[#88D0FF]"
          }`}
          size="lg"
          onClick={handleSubmit}
        >
          제출할래요
        </Button>
      </div>
    </div>
  );
};

export { StepTwo };
